/**
 * OrdersPage
 *
 * Unified Orders page with expandable order rows, batch creation flow,
 * URL-based filter persistence, and server-side filtering + pagination.
 *
 * Features:
 * - Server-side filtering via API (search, age, type, tags, colors, sizes, fulfillment)
 * - Server-side pagination with Previous/Next controls
 * - Expandable order rows with line item details
 * - Animated popover filters with URL persistence
 * - Animated sticky action button (Create Batch)
 * - BLU-777: operator-configurable batch SCOPES via `Create Batch (N) ▾`, with a
 *   server-resolved preview. The legacy Auto Batch button and the P&P reminder
 *   banner are gone (they were the only two entry points to the SKU-affinity
 *   auto-batcher, whose selection cannot describe a ticked union truthfully).
 * - Comprehensive error handling and loading states
 * - Location change clears selection (prevents cross-location batch)
 * - Filter change clears selection with toast notification
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { FloatingSelectionBar } from '@/components/layout/FloatingSelectionBar';
import { Loader2, AlertTriangle, ChevronLeft, ChevronRight, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useRole } from '@/hooks/use-role';
import { canOpenIssuesPage } from '@/lib/roles';
import { useBatchNotification } from '@/contexts/BatchNotificationContext';
import { OrderListHeader, ExpandableOrderRow, OrderQueueColumnHeader, QueueDisplayMenu } from './orders';
import { buildQueueColumns } from './orders/queue-columns';
import { OrdersFilterBar } from './OrdersFilterBar';
import { BatchPreview } from './BatchPreview';
import { BatchInclusionToggle } from './BatchInclusionToggle';
import { BatchScopeMenu } from './BatchScopeMenu';
import { usePaginatedOrderQueue, useFilterOptions, type QueueTab } from '../hooks/useOrderQueue';
import type { QueuedOrder } from '../types';
import { useOrderSelection } from '../hooks/useOrderSelection';
import { useInitialClientChoice, useOrderFilters } from '../hooks/useOrderFilters';
import { useExpandableOrders } from '../hooks/useExpandableOrders';
import { useOrderQueueDisplay } from '../hooks/useOrderQueueDisplay';
import { useQueueTier } from '../hooks/useQueueTier';
import { useCreateBatch, useAutoBatchPreview, useBatchScopes } from '../hooks/useBatches';
import {
  BATCH_CREATION_OUTCOME_TOAST_ID,
  toastBatchTooLarge,
  toastOrderTagsConflict,
  useBatchCreationStatus,
} from '../hooks/useBatchCreationStatus';
import { useSupersedeBatchConfirm } from '../hooks/useSupersedeBatchConfirm';
import { useBatchCreatedToast, type BatchCreatedInfo } from '../hooks/useBatchCreatedToast';
import { SupersedeBatchConfirmDialog } from './SupersedeBatchConfirmDialog';
import { BatchCreationProgress } from './BatchCreationProgress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { trpc } from '@/lib/trpc';
import { isBatchCreationInProgressError, isDtfOrderTagsUnreadableError, isUncertainOutcomeError } from '@/lib/trpc-error-utils';
import { cn } from '@/lib/utils';
import { PageLoader } from '@/components/loading/PageLoader';
import { useClientOrgFilter, ALL_CLIENTS } from '@/features/inventory/hooks/useClientOrgFilter';
import { ClientFilterDropdown } from '@/features/inventory/components/ClientFilterDropdown';
import { isOwnedFloor } from '@/features/inventory/hooks/useOwnedFloorIds';
import { isSelectableForBatching } from '../utils/order-eligibility';
import { ManualOrderQueueActions } from '@/features/manual-orders/components/ManualOrderQueueActions';
import { updateSearchParams } from '@/lib/search-params';
import { WaitingOnBlankButton } from './WaitingOnBlankButton';
import { WaitingOnBlankList } from './WaitingOnBlankList';
import { useWaitingOnBlankCount } from '../hooks/useWaitingOnBlank';
import { QUEUE_VIEW_PARAM, WAITING_BLANK_VIEW, WAITING_SEARCH_PARAM } from '../utils/waiting-on-blank';

// ============================================================================
// COMPONENT
// ============================================================================

/** What a create path learns from the createBatch response (absent when the response was lost). */
type CreatedBatch = Pick<BatchCreatedInfo, 'batchId' | 'justInTime'>;

export function OrdersPage() {
  const { t } = useTranslation('production');
  // Workflow settings from server (pagination mode + page size)
  const utils = trpc.useUtils();

  // Put-order-on-hold (the order row's ⋮ kebab). Order-centric — holds by
  // prod_order_id, so it works for any queue order (no OOS issue required).
  const holdOrderMutation = trpc.productionOrders.issues.holdOrder.useMutation({
    onSuccess: () => {
      void utils.productionOrders.getOrderQueue.invalidate();
      void utils.productionOrders.getQueueCounts.invalidate();
      void utils.productionOrders.issues.getCounts.invalidate();
      // The held order now lives on the On-Hold tab — refresh it so a coordinator
      // who switches there immediately sees it (mirrors useSendBackToProduction).
      void utils.productionOrders.issues.getOnHoldOrders.invalidate();
    },
  });
  const handlePutOnHold = useCallback(
    (order: QueuedOrder) => {
      holdOrderMutation.mutate(
        { prod_order_id: order.id, hold_reason: 'manual' },
        {
          onSuccess: () => toast.success(t('orders.actions.putOnHoldSuccess')),
          onError: (error) =>
            toast.error(error.message || t('orders.actions.putOnHoldFailed')),
        }
      );
    },
    [holdOrderMutation, t]
  );

  // Skip-waiting-period (the order row's ⋮ kebab). Clears ONE order's 2-hour
  // Shopify-editing hold (held_until) so it's immediately batchable, even
  // when the location default keeps the hold ON.
  const skipHoldMutation = trpc.productionOrders.issues.skipOrderHold.useMutation({
    onSuccess: () => {
      void utils.productionOrders.getOrderQueue.invalidate();
      void utils.productionOrders.getQueueCounts.invalidate();
    },
  });
  const handleSkipHold = useCallback(
    (order: QueuedOrder) => {
      skipHoldMutation.mutate(
        { prod_order_id: order.id },
        {
          onSuccess: () => toast.success(t('orders.actions.skipHoldSuccess')),
          onError: (error) =>
            toast.error(error.message || t('orders.actions.skipHoldFailed')),
        }
      );
    },
    [skipHoldMutation, t]
  );
  const { data: workflowSettings } = trpc.productionSettings.getWorkflowSettings.useQuery(
    undefined,
    { staleTime: 5 * 60 * 1000 }
  );
  const updateWorkflowSettings = trpc.productionSettings.updateWorkflowSettings.useMutation({
    onSuccess: () => utils.productionSettings.getWorkflowSettings.invalidate(),
  });
  const paginationMode = workflowSettings?.orderQueuePaginationMode ?? 'paginated';

  // Local page size state for immediate UI feedback (avoids dropdown snap-back
  // while waiting for server mutation round-trip)
  const [pageSize, setPageSize] = useState(30);
  useEffect(() => {
    if (workflowSettings?.orderQueuePageSize) {
      setPageSize(workflowSettings.orderQueuePageSize);
    }
  }, [workflowSettings?.orderQueuePageSize]);

  // Mode state: browse (normal view) or scoped-preview (BLU-777 batch scopes).
  //
  // ⚠ The old 'auto-preview' mode is GONE, along with the Auto Batch button and
  // the P&P banner that were its only two entry points. That was deliberate: the
  // auto-batcher selects by SKU affinity on a garment budget, force-adds P&P
  // orders nobody ticked, drops bench/B2B orders that WERE ticked, and is
  // single-org in three places (every Mini Zali order at Ningbo). The scoped flow
  // reuses this preview SCREEN but never that selection — the server resolves the
  // union and the preview is handed an explicit `orderIds` list.
  const [mode, setMode] = useState<'browse' | 'scoped-preview'>('browse');
  // Seed from the URL's ?search= (shared filter URLs; order-message
  // notifications now open the Client messages inbox instead). Without this, the mount-time
  // debounce-sync below overwrites the URL-derived filter with '' and the
  // deep link is silently wiped. Lazy read — the input stays uncontrolled by
  // the URL after mount (useOrderFilters owns the write-back).
  const [searchInput, setSearchInput] = useState(
    () => new URLSearchParams(window.location.search).get('search') ?? '',
  );

  // Debounce search input (300ms) to avoid excessive API calls
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Context hooks - CRITICAL: Use selectedLocation NOT currentLocation
  const { selectedLocation, allLocations } = useLocation();
  const { organization } = useOrganization();
  const { notifyBatchCreated } = useBatchNotification();

  const locationId = selectedLocation?.id;
  const shopifyDomain = organization?.shopify_domain;
  // BLU-1423: the client this floor's Orders page opens on. Read from the live
  // list, not the `selectedLocation` snapshot, so a save in Settings reaches
  // this page without a reload.
  const floorDefaultClientOrgId =
    (allLocations.find((loc) => loc.id === locationId) ?? selectedLocation)?.queue_default_client_org_id ?? null;
  const initialClientChoice = useInitialClientChoice();

  // BLU-367/BLU-787: cross-org client filter, now a QUERY INPUT rather than a
  // browser-side array filter. The first cut filtered the loaded page in the
  // browser while `total`/`totalPages` came back unfiltered, so the pager lied
  // (BLU-389 Phase A.2 bug). Mirrors `ProductionTrackerPage.tsx`'s block —
  // that is the canonical reference for this pattern.
  //
  // `rows: []` + `resetWhenAbsent: false` are both deliberate: the option list
  // comes from the connected-client roster (`orgIdToName`), never from rows
  // that are now already server-filtered by the selection, and once filtering
  // is server-side a client with zero ready orders returns zero rows — the
  // default reset-on-absent behaviour would read that as "the selected org
  // vanished" and silently widen the batch back to the whole floor.
  //
  // BLU-1423: the page opens on the floor's default client. A person's own
  // pick wins and lives in the URL (`?client=`); a LOCATION change drops it,
  // because the chosen client may not be connected at the new floor, and the
  // new floor opens on its own default (`scopeKey`).
  const clientFilter = useClientOrgFilter<{ organization_id?: string | null }>({
    rows: [],
    ownOrgId: organization?.id,
    ownOrgName: organization?.name,
    locationId,
    resetWhenAbsent: false,
    defaultOrgId: floorDefaultClientOrgId,
    scopeKey: locationId,
    initialChoice: initialClientChoice,
  });
  const clientOrgFilter = clientFilter.clientOrgFilter;
  const setClientOrgFilter = clientFilter.setClientOrgFilter;
  const isClientFiltered = clientOrgFilter !== ALL_CLIENTS;
  /** Sent to the server. Undefined = all clients. */
  const clientOrgFilterParam = isClientFiltered ? clientOrgFilter : undefined;
  const showClientFilter = clientFilter.showClientFilter;

  // ONE roster source. `useClientOrgLabels` (admin-only `listConnectedClients`)
  // and `useClientOrgFilter` (location-scoped `listConnectedClientsAtLocation`,
  // usable by Coordinator/Staff too) used to feed this page's dropdown and its
  // `orgIdToNameRecord` from two DIFFERENT queries — two rosters that can
  // disagree. `ExpandableOrderRow` still wants a plain Record; derive it from
  // the one roster the filter itself uses.
  const orgIdToNameRecord = useMemo(
    () => Object.fromEntries(clientFilter.orgIdToName),
    [clientFilter.orgIdToName]
  );
  // Options come from the connected-client roster, not from orgs present in
  // the (now server-filtered) loaded page — deriving from rows would be
  // circular once the rows are already narrowed by this same selection.
  const clientFilterOptions = useMemo(
    () => Array.from(clientFilter.orgIdToName.keys()),
    [clientFilter.orgIdToName]
  );

  // Filter hook with URL persistence (no orders dependency - server-side filtering)
  const {
    filters,
    setFilters,
    apiFilters,
    scopeFilters,
  } = useOrderFilters({
    persistToUrl: true,
    clientOrganizationId: clientOrgFilterParam,
    clientChoice: clientFilter.pickedOrgFilter,
  });

  // Queue status and sort order derived from filters for API
  const queueStatusForApi: QueueTab = filters.queueStatus;

  // Sync debounced search to filter state (uses updater to avoid stale closure)
  useEffect(() => {
    setFilters(prev => {
      if (debouncedSearch !== prev.searchQuery) {
        return { ...prev, searchQuery: debouncedSearch };
      }
      return prev;
    });
  }, [debouncedSearch, setFilters]);

  // Data fetching with server-side filters (including the client filter) and
  // pagination. `usePaginatedOrderQueue`'s own filtersJson-diff effect already
  // resets to page 0 whenever `apiFilters` changes — now that the client
  // filter is folded into `apiFilters`, that generic reset covers it too, the
  // same way it already covers every other filter on this screen.
  const {
    data: orders,
    accumulatedOrders,
    isLoading: ordersLoading,
    isFetching,
    error: ordersError,
    refetch: refetchOrders,
    total,
    hasMore,
    currentPage,
    totalPages,
    goToPage,
    loadMore,
    reset: resetOrderPage,
  } = usePaginatedOrderQueue(locationId, queueStatusForApi, pageSize, apiFilters, filters.sortOrder);

  // Explicit belt-and-suspenders reset for the client filter specifically:
  // without it, an operator on page 4 who filters to one client would land on
  // page 4 of what may now be a 1-page list and see nothing. This duplicates
  // (harmlessly — `reset` is idempotent) the generic filtersJson-diff reset
  // `usePaginatedOrderQueue` already runs for every `apiFilters` change, now
  // that the client filter is folded into it; kept explicit so this specific
  // case can never silently stop working if that wiring ever changes.
  useEffect(() => {
    resetOrderPage();
  }, [clientOrgFilter, resetOrderPage]);

  // Filter options from dedicated endpoint (static, cache-friendly)
  const { data: filterOptions } = useFilterOptions(locationId);

  // In endless mode, show accumulated orders; in paginated mode, show current
  // page. Already filtered server-side (the client filter is a query input),
  // so no browser-side pass is needed here.
  const currentOrders = useMemo(
    () => (paginationMode === 'endless' ? accumulatedOrders : orders) ?? [],
    [paginationMode, accumulatedOrders, orders]
  ) as NonNullable<typeof orders>;

  // Client chat icon + unread dot on client rows. The input must stay exactly
  // `{ orderIds: [] }` so it shares the sidebar's Client messages cache entry:
  // the sidebar polls it, this page only reads it (refetchOnMount false, no
  // interval), so the dot costs no extra request. Disabled on pages with no
  // client rows. Row order_id (ERP orders.id) is the unreadByOrderId key.
  const { can, role } = useRole();
  const canOpenClientChat = can('orders.view');
  // Phase 104 (D-21): the manual order edit page's guard (same holders as New order).
  const canEditManualOrders = can('orders.create');

  // BLU-1506: the Waiting on blank view (`?view=waiting_blank`, its own
  // `?waiting_search=`). Only the floor's owner has one. A 3PL client at a
  // shared floor never asks (not an owned floor); anyone else the server answers
  // isOwner:false, which hides the button and closes the view. A deep link
  // opens the view at once and closes it if that answer comes back false.
  const [searchParams, setSearchParams] = useSearchParams();
  const isOwnerSide = !!selectedLocation && isOwnedFloor(selectedLocation, organization?.id);
  const waiting = useWaitingOnBlankCount(locationId, isOwnerSide);
  const waitingViewOpen =
    isOwnerSide &&
    (waiting.isOwner || !waiting.answered) &&
    searchParams.get(QUEUE_VIEW_PARAM) === WAITING_BLANK_VIEW;
  const waitingSearch = searchParams.get(WAITING_SEARCH_PARAM) ?? '';
  // Open and close push a history entry, so Back returns to where you were.
  const setWaitingView = useCallback(
    (open: boolean) =>
      setSearchParams((prev) =>
        updateSearchParams(prev, {
          [QUEUE_VIEW_PARAM]: open ? WAITING_BLANK_VIEW : null,
          [WAITING_SEARCH_PARAM]: null,
        }),
      ),
    [setSearchParams],
  );
  const closeWaitingView = useCallback(() => setWaitingView(false), [setWaitingView]);
  const setWaitingSearch = useCallback(
    (value: string) =>
      setSearchParams((prev) => updateSearchParams(prev, { [WAITING_SEARCH_PARAM]: value.trim() || null }), {
        replace: true,
      }),
    [setSearchParams],
  );
  const showWaitingButton = isOwnerSide && waiting.isOwner && (waiting.count > 0 || waitingViewOpen);
  const hasClientRows = useMemo(
    () => !!organization?.id && currentOrders.some((o) => o.organization_id !== organization.id),
    [currentOrders, organization?.id]
  );
  const clientChatMeta = trpc.orderMessages.inboxMeta.useQuery(
    { orderIds: [] },
    { enabled: canOpenClientChat && hasClientRows, refetchOnMount: false, staleTime: 30_000 }
  );
  const unreadByOrderId = clientChatMeta.data?.unreadByOrderId;

  // The OOS badge links into the Issues page; its RoleGuard silently
  // redirects a role it denies, so only roles it admits get the link.
  const canOpenIssues = canOpenIssuesPage(role);

  // BLU-718: orders still inside the customer-editing hold window cannot be
  // batched — createBatch rejects the WHOLE batch if one is included. Recompute
  // on a 30s tick (matching the per-row countdown) so a row becomes selectable
  // the moment its hold expires; the queue query has no refetchInterval, so
  // nothing else would re-render this list.
  const hasHeldOrders = useMemo(
    () => currentOrders.some((o) => o.held_until),
    [currentOrders]
  );
  const [holdTick, setHoldTick] = useState(0);
  useEffect(() => {
    if (!hasHeldOrders) return;
    const interval = setInterval(() => setHoldTick((n) => n + 1), 30_000);
    return () => clearInterval(interval);
  }, [hasHeldOrders]);

  const unselectableOrderIds = useMemo(() => {
    const now = Date.now();
    const ids = new Set<string>();
    for (const o of currentOrders) {
      if (o.held_until && new Date(o.held_until).getTime() > now) ids.add(o.id);
      // An order whose live garment units already cover it cannot be batched:
      // Create Batch refuses it (the duplicate-production guard), so letting
      // select-all sweep it in would fail the whole batch. Same reason held
      // orders are here. Shared predicate with the row's own disabled
      // checkbox, so the two can never disagree about which rows are inert.
      if (!isSelectableForBatching(o)) ids.add(o.id);
    }
    return ids;
    // holdTick is an intentional recompute trigger, not a value read here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrders, holdTick]);

  const {
    selectedIds,
    toggleSelect,
    selectAll,
    clearSelection,
    isAllSelected,
    isSomeSelected,
    selectedCount,
  } = useOrderSelection({ orders: currentOrders, unselectableIds: unselectableOrderIds });

  // Expandable orders state
  const orderIds = useMemo(() => currentOrders.map(o => o.id), [currentOrders]);
  const {
    isExpanded,
    toggleExpand,
    expandAll,
    collapseAll,
    allExpanded,
    noneExpanded,
  } = useExpandableOrders({ orderIds });

  // Selection cleanup when filters change is handled by useOrderSelection:
  // it removes selections for orders no longer in the visible set,
  // while preserving selections for orders that are still visible.

  // BLU-777 / closes BLU-632: the two injection gates, split. Both default true
  // (legacy behaviour). This is the LIVE batch surface, so they live here.
  //
  // ⚠ Declared ABOVE the location-change effect below, which resets them. These
  // are `const`, so a reference from an effect declared earlier in the file is a
  // TDZ error at render, not a lint warning.
  const [includeRedos, setIncludeRedos] = useState(true);
  const [includeRequeued, setIncludeRequeued] = useState(true);

  /**
   * BLU-787 — unticking an injection is a ONE-BATCH decision.
   *
   * Both gates default ON and nothing used to turn them back on, so an operator
   * who unticked Redos once excluded them from every subsequent batch for as
   * long as the page stayed mounted, with nothing on screen saying so. It even
   * survived a location change: untick at Ningbo, switch to HQ, still off.
   *
   * Redos are reprints of work already promised to a customer. A silent,
   * indefinite opt-out is exactly how they stop getting made. Brett's call
   * 2026-08-13: reset after every successful create and on location change.
   */
  const resetInjectionGates = useCallback(() => {
    setIncludeRedos(true);
    setIncludeRequeued(true);
  }, []);

  // CRITICAL: Clear selection and search when location changes (prevent cross-location batch)
  const prevLocationIdRef = useRef<string | undefined>(locationId);
  useEffect(() => {
    if (prevLocationIdRef.current !== locationId) {
      // Clear search input when location changes
      if (searchInput) {
        setSearchInput('');
      }
      // Clear selection when location changes (if any selected)
      if (selectedCount > 0) {
        clearSelection();
        toast.info(t('orders.selectionCleared'));
      }
      // BLU-787: the injection gates are per-batch, and a location is a
      // different floor with different redos. Carrying an untick across the
      // switch is how redos silently stop being made at the new location.
      resetInjectionGates();
    }
    prevLocationIdRef.current = locationId;
  }, [locationId, selectedCount, clearSelection, searchInput, resetInjectionGates]);

  // Clear selection when page changes (prevent stale selections across pages)
  const prevPageRef = useRef(currentPage);
  useEffect(() => {
    if (prevPageRef.current !== currentPage) {
      prevPageRef.current = currentPage;
      clearSelection();
    }
  }, [currentPage, clearSelection]);

  // Batch creation mutation - use existing hook from useBatches.ts
  const createBatchMutation = useCreateBatch();

  // BLU-1330: live "X of Y garments saved" while a create runs, the same
  // ticker after a reload mid-batch, and the outcome when the createBatch
  // response is lost (up to 120 s through the China proxy). `blocksCreate`
  // (createBatch's isPending included) replaces every isPending gate below:
  // while any run of this org holds this location, mine or a colleague's, no
  // create control may start another. The server enforces the same rule.
  const {
    view: batchCreationView,
    blocksCreate,
    beginRun,
    beginChecking,
    watchLocation,
  } = useBatchCreationStatus(locationId, { isCreating: createBatchMutation.isPending });

  // Re-batch supersede gate: if a re-batch would invalidate already-printed
  // stickers, the backend throws SUPERSEDE_CONFIRM_REQUIRED and this shows the
  // confirm dialog, then retries with confirmSupersede:true.
  const supersede = useSupersedeBatchConfirm();
  // The one "batch created" toast, with the batch's just-in-time POs (BLU-1268).
  const announceBatchCreated = useBatchCreatedToast();
  // Latest-handler refs so the confirmed-retry closures stay lint-clean (the
  // handlers can't list themselves in their own useCallback deps).
  const handleCreateBatchRef = useRef<(confirmSupersede: boolean) => void>(() => {});
  const handleAutoBatchConfirmRef = useRef<(confirmSupersede: boolean) => void>(() => {});
  // BLU-787: the direct-create path needs its own retry ref, so the supersede
  // dialog replays THIS handler's arguments rather than the preview handler's.
  const handleScopedCreateNowRef = useRef<(confirmSupersede: boolean) => void>(() => {});
  const [trimConfirmOpen, setTrimConfirmOpen] = useState(false);

  // Garment-ceiling refusal: the backend throws `BATCH_TOO_LARGE:<count>:<max>`
  // when one batch would exceed the garment cap. That is a by-design guard, not
  // a fault, so it gets the operator's numbers and the way out instead of the
  // raw marker, stays on screen until dismissed (three sentences, read in zh as
  // often as en), and is kept out of Sentry. Shared by all three create paths,
  // and by a failed run found while checking (useBatchCreationStatus).
  const showBatchTooLarge = useCallback(
    (error: unknown): boolean => toastBatchTooLarge(t, error),
    [t]
  );

  /**
   * The ONE createBatch call, shared by all three create paths (manual
   * selection, scoped preview, scoped create-now) and the only place a
   * `clientRunId` is made: a fresh one per press, so a supersede retry is a
   * new run. `onCreated` is the path's own success (clear, notify, toast);
   * `retryWithSupersede` replays the path with confirmSupersede=true.
   * `mode` is the Sentry tag only; every path sends createBatch mode 'manual'.
   */
  const runCreateBatch = useCallback(async (
    orderIds: string[],
    {
      confirmSupersede,
      mode,
      onCreated,
      retryWithSupersede,
    }: {
      confirmSupersede: boolean;
      mode: 'manual' | 'scoped' | 'scoped-direct';
      onCreated: (batchNumber: number, created?: CreatedBatch) => void;
      retryWithSupersede: () => void;
    }
  ) => {
    if (!locationId) return;
    const clientRunId = crypto.randomUUID();
    beginRun(clientRunId);

    try {
      const result = await createBatchMutation.mutateAsync({
        locationId,
        orderIds,
        mode: 'manual',
        includeRedos,
        includeRequeued,
        confirmSupersede,
        clientRunId,
      });
      // A sticky "Couldn't confirm" or failure from an earlier press must not
      // sit beside this success.
      toast.dismiss(BATCH_CREATION_OUTCOME_TOAST_ID);
      onCreated(result.batch_number, { batchId: result.id, justInTime: result.justInTime });
    } catch (error) {
      // Already-printed stickers? Open the confirm dialog and retry on confirm.
      if (!confirmSupersede && supersede.intercept(error, retryWithSupersede)) return;
      if (showBatchTooLarge(error)) return;
      // An order carries both a DTF tag and a DTG tag, so nothing was batched.
      // The operator fixes the tags or leaves the order out; name the orders.
      if (toastOrderTagsConflict(t, error)) return;
      // Another run holds this location: nothing was started. Say so, and
      // watch the location so Create Batch unlocks itself when it frees.
      if (isBatchCreationInProgressError(error)) {
        toast.info(t('batch.progress.locationBusyToast'), { id: BATCH_CREATION_OUTCOME_TOAST_ID, duration: 6_000 });
        watchLocation();
        return;
      }
      const uncertain = isUncertainOutcomeError(error);
      Sentry.captureException(error, {
        tags: { feature: 'batch-creation', mode, outcome: uncertain ? 'checking' : 'failed' },
        extra: { locationId, orderCount: orderIds.length },
      });
      // No verdict from the server: the batch may well exist. Find out.
      if (uncertain) {
        beginChecking(clientRunId, onCreated);
        return;
      }
      // The DTF order tag setting could not be read, so nothing was batched.
      // The server's text is English-only; show its own translated copy.
      if (isDtfOrderTagsUnreadableError(error)) {
        toast.error(t('batch.progress.dtfTagsUnreadable'), { id: BATCH_CREATION_OUTCOME_TOAST_ID, duration: Infinity });
        return;
      }
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error(t('orders.failedToCreateBatch', { message }));
    }
  }, [locationId, includeRedos, includeRequeued, createBatchMutation, supersede, showBatchTooLarge, beginRun, beginChecking, watchLocation, t]);


  // ------------------------------------------------------------------------
  // BLU-777 — configurable batch scopes.
  //
  // ⚠ OWN STATE, DELIBERATELY NOT `useOrderSelection`. That hook is constructed
  // with `orders: currentOrders` and PRUNES any id not in that array, and the
  // page clears the whole selection on page change. Routing ticked scopes
  // through it would trim a 48-order union down to the ~4 Express rows on the
  // loaded page — with no error anywhere. Scope ticks are rules, not rows.
  // ------------------------------------------------------------------------
  const [checkedScopeIds, setCheckedScopeIds] = useState<Set<string>>(new Set());

  const toggleScope = useCallback((scopeId: string) => {
    setCheckedScopeIds((prev) => {
      const next = new Set(prev);
      if (next.has(scopeId)) next.delete(scopeId);
      else next.add(scopeId);
      return next;
    });
  }, []);
  const clearScopes = useCallback(() => setCheckedScopeIds(new Set()), []);

  const checkedScopeIdList = useMemo(() => Array.from(checkedScopeIds), [checkedScopeIds]);

  // Hand-ticked rows add INTO the union (L4). Sent to the resolver ONLY once a
  // scope is ticked: with no scope ticked the existing manual floating-bar flow
  // owns the selection, and threading it through here would re-run the resolver
  // on every checkbox click in the table for no change in what gets created.
  const extraOrderIdsForScopes = useMemo(
    () => (checkedScopeIdList.length > 0 ? Array.from(selectedIds) : undefined),
    [checkedScopeIdList.length, selectedIds]
  );

  // Server-resolved scope counts + union. Runs whenever a location is selected
  // because the menu shows a live count beside every scope before anything is
  // ticked. Net query cost is flat vs before: the P&P banner this replaces was
  // firing the FULL auto-batch preview on every queue load to feed itself.
  //
  // BLU-787: `scopeFilters` (search-stripped `apiFilters`) makes the batch
  // follow the operator's live queue filter, including the client filter.
  const {
    data: scopeResolution,
    isFetching: scopesFetching,
    isError: scopesFailed,
    refetch: refetchScopes,
  } = useBatchScopes(locationId, checkedScopeIdList, extraOrderIdsForScopes, scopeFilters);

  const scopes = scopeResolution?.scopes ?? [];
  const scopedOrderIds = useMemo(() => scopeResolution?.orderIds ?? [], [scopeResolution?.orderIds]);
  const scopeCap = scopeResolution?.cap ?? 200;
  // Sum of the ticked scopes' own counts — the menu compares it with the union to
  // explain the difference ("31 orders are in more than one scope") instead of
  // letting the smaller number look like a bug.
  const tickedScopeSum = useMemo(
    () => scopes.filter((sc) => checkedScopeIds.has(sc.id)).reduce((sum, sc) => sum + sc.count, 0),
    [scopes, checkedScopeIds]
  );

  // Scoped preview: hands the RESOLVED ids to the backend, which then bypasses
  // auto-batch candidate selection entirely.
  const { data: autoBatchPreview, isLoading: previewLoading } = useAutoBatchPreview(
    locationId,
    mode === 'scoped-preview' && scopedOrderIds.length > 0,
    undefined,
    scopedOrderIds
  );

  const handleOpenScopedPreview = useCallback(() => {
    if (scopedOrderIds.length === 0) {
      toast.error(t('batch.scopes.nothingToBatch'));
      return;
    }
    setMode('scoped-preview');
  }, [scopedOrderIds.length, t]);

  // Handle manual batch creation with error handling
  const handleCreateBatch = useCallback(async (confirmSupersede = false) => {
    if (!locationId) {
      toast.error(t('orders.selectLocationFirst'));
      return;
    }

    if (selectedIds.size === 0) {
      toast.error(t('batch.selectAtLeastOne'));
      return;
    }

    const orderCount = selectedIds.size;
    // The one success path: the createBatch response, or (when that response
    // was lost) the progress poll finding the run done.
    const onCreated = (batchNumber: number, created?: CreatedBatch) => {
      // Clear selection after successful batch creation
      clearSelection();
      // BLU-787: an untick applies to THIS batch only — see resetInjectionGates.
      resetInjectionGates();

      // Notify to trigger green indicator on Picker nav
      notifyBatchCreated(String(batchNumber));

      announceBatchCreated({ batchNumber, orderCount, ...created });
    };

    await runCreateBatch(Array.from(selectedIds), {
      confirmSupersede,
      mode: 'manual',
      onCreated,
      retryWithSupersede: () => handleCreateBatchRef.current(true),
    });
  }, [locationId, selectedIds, clearSelection, resetInjectionGates, notifyBatchCreated, announceBatchCreated, runCreateBatch, t]);
  handleCreateBatchRef.current = handleCreateBatch;

  // BLU-777: create the SCOPED batch from exactly what the preview is showing.
  //
  // ⚠ `mode: 'manual'`, not 'auto'. `mode` is log-only (two logger.info calls;
  // prod_batches has no such column) but a scoped batch is an operator-chosen,
  // explicitly-confirmed set — logging it as 'auto' would have the logs claim the
  // algorithm picked these orders.
  //
  // ⚠ Sends `autoBatchPreview.orders`, NOT `scopedOrderIds`. The two agree today,
  // but the preview response is the set the operator actually looked at and
  // confirmed; if the resolver refetched between preview and confirm, the screen
  // is the contract.
  const handleScopedBatchConfirm = useCallback(async (confirmSupersede = false) => {
    if (!locationId || !autoBatchPreview) {
      toast.error(t('orders.unableToCreateBatch'));
      return;
    }

    const orderCount = autoBatchPreview.orders.length;
    const onCreated = (batchNumber: number, created?: CreatedBatch) => {
      setMode('browse');
      clearScopes();
      clearSelection();
      // BLU-787: an untick applies to THIS batch only — see resetInjectionGates.
      resetInjectionGates();
      notifyBatchCreated(String(batchNumber));
      announceBatchCreated({ batchNumber, orderCount, ...created });
    };

    await runCreateBatch(autoBatchPreview.orders.map(o => o.id), {
      confirmSupersede,
      mode: 'scoped',
      onCreated,
      retryWithSupersede: () => handleAutoBatchConfirmRef.current(true),
    });
  }, [locationId, autoBatchPreview, clearScopes, clearSelection, resetInjectionGates, notifyBatchCreated, announceBatchCreated, runCreateBatch, t]);
  handleAutoBatchConfirmRef.current = handleScopedBatchConfirm;

  /**
   * BLU-787 — create straight from the resolved union, no preview round-trip.
   *
   * Brett, 2026-08-13: "why do I have to preview before I create a batch?"
   * Preview is still there and still first; this is the fast path for an
   * operator who already knows what they ticked.
   *
   * ⚠ Sends `scopedOrderIds`, the SERVER's resolved union — already deduped,
   * oldest-first and cap-trimmed by `resolveScopedUnion`. Never `selectedIds`,
   * which `useOrderSelection` prunes to the loaded page (the PR #1659 class), and
   * never a browser-side recount.
   *
   * What skipping the preview actually costs, having traced it: almost nothing.
   * `getOrdersForBatchPreviewByIds` does NOT re-validate status — it hardcodes
   * `production_status: 'NEW'`, `batch_id: null`, `eligibility_status: 'ELIGIBLE'`
   * on every row it returns. The only rows it drops are ones no longer at this
   * (org, location), which without it surface as `createBatch`'s "Some orders
   * could not be found or are not accessible". Its sort is display-only;
   * `createBatch` re-derives unit order itself. So this is not a safety net being
   * removed, and the same `staleTime` applies to both paths.
   *
   * The trim IS worth stopping for, because nothing downstream will now show it:
   * over the 200 cap the operator gets a confirm naming the number before it
   * commits. On live counts (`custom_product` 456) that is a day-one path.
   */
  const runScopedCreate = useCallback(async (confirmSupersede = false) => {
    if (!locationId || scopedOrderIds.length === 0) {
      toast.error(t('orders.unableToCreateBatch'));
      return;
    }

    const orderCount = scopedOrderIds.length;
    const onCreated = (batchNumber: number, created?: CreatedBatch) => {
      clearScopes();
      clearSelection();
      resetInjectionGates();
      notifyBatchCreated(String(batchNumber));
      announceBatchCreated({ batchNumber, orderCount, ...created });
    };

    await runCreateBatch(scopedOrderIds, {
      confirmSupersede,
      mode: 'scoped-direct',
      onCreated,
      retryWithSupersede: () => handleScopedCreateNowRef.current(true),
    });
  }, [locationId, scopedOrderIds, clearScopes, clearSelection, resetInjectionGates, notifyBatchCreated, announceBatchCreated, runCreateBatch, t]);
  handleScopedCreateNowRef.current = runScopedCreate;

  /**
   * The trim gate. Split from `runScopedCreate` so the supersede retry replays
   * the CREATE, not the gate — otherwise confirming supersede would re-open the
   * trim dialog behind it.
   *
   * ⚠ The trim confirm is an AlertDialog, never `window.confirm`. This action
   * already routes its other confirm (supersede) through one, so a union that
   * both trims and hits printed stickers would show an OS box followed by a
   * styled dialog for a single press. `window.confirm` also runs synchronously
   * inside Radix's `onSelect` while the menu dismisses with `pointer-events:
   * none` on the body, and Chrome's "prevent this page from creating additional
   * dialogs" auto-returns FALSE — so the operator's create would silently do
   * nothing, the worst failure available on a control built to be pressed fast.
   */
  const handleScopedCreateNow = useCallback(() => {
    if (scopeResolution?.trimmed) {
      setTrimConfirmOpen(true);
      return;
    }
    void runScopedCreate(false);
  }, [scopeResolution?.trimmed, runScopedCreate]);

  // Wrap callbacks in useCallback for optimization
  const handleToggleSelect = useCallback((orderId: string, event?: React.MouseEvent | React.KeyboardEvent) => {
    toggleSelect(orderId, event);
  }, [toggleSelect]);

  const handleExpandAll = useCallback(() => {
    expandAll(orderIds);
  }, [expandAll, orderIds]);

  const handleCollapseAll = useCallback(() => {
    collapseAll();
  }, [collapseAll]);

  // Infinite scroll: auto-load when sentinel becomes visible
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Queue columns. The list's own scroll container is measured (one
  // ResizeObserver for the page) and is still the infinite-scroll root.
  // useQueueTier sets state only when the tier changes, so this page does not
  // re-render on each frame of the sidebar animation; the layout object
  // changes only with the tier or a setting, so the memoised rows and header
  // re-render only then too.
  const display = useOrderQueueDisplay();
  // Connected clients load separately from the queue, so client rows can
  // arrive before showClientFilter turns true.
  const showClientColumns = showClientFilter || hasClientRows;
  const showChat = showClientColumns && canOpenClientChat;
  const showType = display.visibility.fulfilmentType;
  const [listTierRef, { density, belowNarrow }] = useQueueTier<HTMLDivElement>({
    showClientColumns,
    showChat,
    showType,
  });
  const setScrollContainer = useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;
      listTierRef(node);
    },
    [listTierRef]
  );
  const queueLayout = useMemo(
    () => buildQueueColumns({ density, belowNarrow, showClientColumns, showChat, showType }),
    [density, belowNarrow, showClientColumns, showChat, showType]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || paginationMode !== 'endless') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isFetching) {
          loadMore();
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isFetching, paginationMode, loadMore]);

  // The Create Batch pill shows for a manual selection in browse mode.
  const showSelectionBar = selectedCount > 0 && mode === 'browse';

  // Track if we're in initial loading state (no data yet)
  const isInitialLoading = ordersLoading && !orders;

  // Error state
  if (ordersError) {
    return (
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>Failed to load orders. Please try again.</span>
            <Button variant="outline" size="sm" onClick={() => refetchOrders()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6 animate-enter">
      {/* Page header with icon box pattern */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <ClipboardList className="h-6 w-6 text-primary" aria-hidden="true" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{t('orders.queue')}</h1>
          <p className="text-muted-foreground">
            {selectedLocation ? t('orders.manageAndBatchAtLocation', { location: selectedLocation.name }) : t('orders.manageAndBatch')}
          </p>
        </div>
      </div>

      {/* Batch scopes + filters — hidden in scoped-preview mode */}
      {mode === 'browse' && (
        <>
          <div className="flex items-center gap-4 flex-wrap">
            {/* BLU-777: ONE control (Concept A). Replaces the Auto Batch button.
                Every count inside it is server-resolved — see BatchScopeMenu. */}
            <BatchScopeMenu
              scopes={scopes}
              checkedScopeIds={checkedScopeIds}
              onToggleScope={toggleScope}
              onClearScopes={clearScopes}
              unionTotal={scopeResolution?.total ?? 0}
              unionTaken={scopeResolution?.taken ?? 0}
              trimmed={scopeResolution?.trimmed ?? false}
              cap={scopeCap}
              // Surviving hand-picks, not the number sent — the resolver drops any
              // id that is no longer batch-eligible.
              handPickedCount={Math.max(
                0,
                (extraOrderIdsForScopes?.length ?? 0) - (scopeResolution?.droppedExtras ?? 0)
              )}
              droppedHandPicked={scopeResolution?.droppedExtras ?? 0}
              tickedScopeSum={tickedScopeSum}
              scopeUnionTotal={scopeResolution?.scopeUnionTotal ?? 0}
              isLoading={scopesFetching}
              isError={scopesFailed}
              onRetry={() => void refetchScopes()}
              disabled={blocksCreate}
              onPreview={handleOpenScopedPreview}
              onCreateNow={handleScopedCreateNow}
              isCreating={blocksCreate}
              includeRedos={includeRedos}
              onIncludeRedosChange={setIncludeRedos}
              includeRequeued={includeRequeued}
              onIncludeRequeuedChange={setIncludeRequeued}
              redoCount={scopeResolution?.pendingRedoCount ?? 0}
              redoTotalPending={scopeResolution?.pendingRedoTotal ?? 0}
              requeuedCount={scopeResolution?.requeuedCount}
              screenSortOrder={filters.sortOrder}
              screenQueueStatus={filters.queueStatus}
              hasActiveSearch={!!filters.searchQuery}
              isClientFiltered={isClientFiltered}
            />
            {/* Phase 104 (D-03 / D-22 / D-26): '+ Manual order', 'Drafts (N)' and
                'Awaiting artwork (N)', next to Create Batch (a different job from the
                one batch control). orders.create holders only, so the badges
                request is never sent for other roles; the route guard and every
                manualOrders procedure re-check. */}
            {can('orders.create') && <ManualOrderQueueActions locationId={locationId} />}
            {/* BLU-1506: orders waiting on a blank from a supplier (the floor
                owner's only). Opens the view below in place of the queue. */}
            {showWaitingButton && (
              <WaitingOnBlankButton
                count={waiting.count}
                open={waitingViewOpen}
                onToggle={() => setWaitingView(!waitingViewOpen)}
              />
            )}
            {/* BLU-367: per-client filter, server-side (BLU-787 — see
                useClientOrgFilter above). The queue's client column renders
                on every page with connected clients OR client rows
                (showClientColumns), not only pages where the loaded rows
                happened to span more than one org — intended. */}
            {showClientFilter && !waitingViewOpen && (
              <ClientFilterDropdown
                value={clientOrgFilter}
                onChange={setClientOrgFilter}
                ownOrgId={organization?.id}
                orgIdToName={clientFilter.orgIdToName}
                distinctOrgsInData={clientFilterOptions}
              />
            )}
            {/* Result count - shows filtered vs total */}
            {total > 0 && !waitingViewOpen && (
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {t('orders.showingOrders', { count: currentOrders.length, total })}
                {isClientFiltered && (
                  <span className="text-threadheads-purple ml-1">
                    {t('orders.filteredToClient', {
                      client: clientFilter.selectedClientName ?? t('orders.selectedClientFallback'),
                    })}
                  </span>
                )}
                {paginationMode !== 'endless' &&
                  totalPages > 1 &&
                  ` ${t('orders.showingCountPaginated', { page: currentPage + 1, totalPages })}`}
              </span>
            )}
          </div>

          {/* Filter bar (the queue's; the waiting view has its own search) */}
          {!waitingViewOpen && (
          <OrdersFilterBar
            filters={filters}
            onChange={setFilters}
            availableColors={filterOptions?.colors ?? []}
            availableSizes={filterOptions?.sizes ?? []}
            availableProductTypes={filterOptions?.productTypes ?? []}
            availableTags={filterOptions?.tags ?? []}
          />
          )}
        </>
      )}

      {/* Content area */}
      {mode === 'browse' ? (
        waitingViewOpen && locationId ? (
          <WaitingOnBlankList
            locationId={locationId}
            timezone={selectedLocation?.timezone ?? null}
            ownOrgId={organization?.id}
            orgIdToName={clientFilter.orgIdToName}
            search={waitingSearch}
            onSearchChange={setWaitingSearch}
            onClose={closeWaitingView}
          />
        ) : isInitialLoading ? (
          <PageLoader variant="production" pool="queue" />
        ) : (
          <Card className="overflow-hidden relative">
            {/* Subtle loading overlay when refetching (filter/page change) */}
            {isFetching && !ordersLoading && (
              <div className="absolute inset-0 bg-background/40 z-10 pointer-events-none" />
            )}

            {/* Order List Header with search, select all, and expand/collapse controls */}
            <OrderListHeader
              totalOrders={total}
              onExpandAll={handleExpandAll}
              onCollapseAll={handleCollapseAll}
              allExpanded={allExpanded}
              noneExpanded={noneExpanded}
              searchValue={searchInput}
              onSearchChange={setSearchInput}
              isAllSelected={isAllSelected}
              isSomeSelected={isSomeSelected}
              selectedCount={selectedCount}
              onSelectAll={selectAll}
              showSelection={true}
              displayMenu={
                <QueueDisplayMenu
                  visibility={display.visibility}
                  onChange={display.setBadge}
                  onReset={display.reset}
                  onOpenChange={display.onMenuOpenChange}
                />
              }
            />

            {/* Expandable Order Rows */}
            {currentOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                {/* BLU-787: the generic "no orders" copy is misleading once the
                    client filter narrows the queue server-side — a client with
                    zero ready orders on THIS page is a normal outcome, not the
                    whole floor being empty. */}
                {isClientFiltered ? (
                  <>
                    <p className="text-muted-foreground">
                      {t('orders.noClientOrders', { client: clientFilter.selectedClientName ?? '' })}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('orders.noClientOrdersHint')}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-muted-foreground">{t('orders.noOrders')}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('orders.noOrdersFilterHint')}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div
                ref={setScrollContainer}
                className={cn(
                  // scroll-pt-9: the sticky column header (33px) plus the 3px
                  // focus ring, so a row scrolled into view on focus is never
                  // hidden under the header.
                  'max-h-[calc(100vh-280px)] overflow-y-auto scroll-pt-9',
                  queueLayout.minWidth && 'overflow-x-auto'
                )}
              >
                {/* Below the narrow minimum the header and rows scroll
                    sideways together inside the card; the page never does. */}
                <div style={queueLayout.minWidth ? { minWidth: queueLayout.minWidth } : undefined}>
                  <OrderQueueColumnHeader layout={queueLayout} />
                  <div className="divide-y">
                    {currentOrders.map((order) => (
                      <ExpandableOrderRow
                        key={order.id}
                        order={order}
                        isExpanded={isExpanded(order.id)}
                        onToggleExpand={toggleExpand}
                        shopifyDomain={shopifyDomain}
                        isSelected={selectedIds.has(order.id)}
                        onToggleSelect={handleToggleSelect}
                        showCheckbox={true}
                        callerOrganizationId={organization?.id}
                        onPutOnHold={handlePutOnHold}
                        onSkipHold={handleSkipHold}
                        orgIdToName={orgIdToNameRecord}
                        canOpenClientChat={canOpenClientChat}
                        canOpenIssues={canOpenIssues}
                        clientChatUnread={order.order_id ? (unreadByOrderId?.[order.order_id] ?? 0) : 0}
                        layout={queueLayout}
                        badgeVisibility={display.visibility}
                        canEditManualOrders={canEditManualOrders}
                      />
                    ))}
                  </div>
                  {/* Infinite scroll sentinel */}
                  {paginationMode === 'endless' && hasMore && (
                    <div ref={sentinelRef} className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pagination controls (paginated mode only) */}
            {paginationMode !== 'endless' && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 0 || isFetching}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {t('orders.previous')}
                </Button>
                <div className="flex items-center gap-3">
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => {
                      const newSize = Number(v);
                      setPageSize(newSize);
                      updateWorkflowSettings.mutate({ orderQueuePageSize: newSize });
                      clearSelection();
                    }}
                  >
                    <SelectTrigger className="h-8 w-[130px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[30, 50, 100, 200].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {t('orders.perPage', { count: n })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {totalPages > 1 && (
                    <span className="text-sm text-muted-foreground tabular-nums">
                      Page {currentPage + 1} of {totalPages}
                    </span>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={!hasMore || isFetching}
                >
                  {t('orders.next')}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </Card>
        )
      ) : (
        /* BLU-777 Scoped Batch Preview — the same screen the auto flow used, fed
           an explicit order list instead of an algorithm's pick. */
        <div className="flex flex-col h-[calc(100vh-12rem)]">
          {/* Sticky Header */}
          <div className="sticky top-0 z-10 bg-background border-b pb-4 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium">{t('batch.scopes.previewTitle')}</h2>
                <p className="text-sm text-muted-foreground">
                  {t('batch.scopes.previewSubtitle', {
                    count: autoBatchPreview?.orders.length ?? 0,
                  })}
                </p>
              </div>
              <Button variant="destructive" onClick={() => setMode('browse')}>
                {t('orders.cancelPreview')}
              </Button>
            </div>
            {/* The cap TRIMS rather than throwing — say so where the operator is
                about to press Create, not only inside the menu they left. */}
            {scopeResolution?.trimmed && (
              <Alert variant="default" className="mt-4 border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 dark:text-amber-300">
                  {t('batch.scopes.trimmedNote', {
                    taken: scopeResolution.taken,
                    total: scopeResolution.total,
                    remaining: scopeResolution.total - scopeResolution.taken,
                    cap: scopeCap,
                  })}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {previewLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : autoBatchPreview ? (
              <BatchPreview
                preview={autoBatchPreview}
                onConfirm={() => handleScopedBatchConfirm()}
                onBack={() => setMode('browse')}
                isCreating={blocksCreate}
                hideActions={true}
                includeRedos={includeRedos}
                onIncludeRedosChange={setIncludeRedos}
                includeRequeued={includeRequeued}
                onIncludeRequeuedChange={setIncludeRequeued}
              />
            ) : (
              <Alert>
                <AlertDescription>
                  Unable to generate preview. Please try again.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Sticky Footer with actions */}
          {autoBatchPreview && !previewLoading && (
            <div className="sticky bottom-0 z-10 bg-background border-t pt-4 mt-4">
              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={() => setMode('browse')}>
                  {t('orders.backToOrders')}
                </Button>
                <Button
                  onClick={() => handleScopedBatchConfirm()}
                  disabled={blocksCreate || autoBatchPreview.orders.length === 0}
                  className="min-w-[200px]"
                >
                  {createBatchMutation.isPending || batchCreationView?.kind === 'checking' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2 motion-reduce:animate-none" />
                      {t('batch.scopes.creating')}
                    </>
                  ) : (
                    /* Assembled from discrete t() calls, never interpolated into a
                       template literal — `t()` inside a template literal is
                       invisible to `npm run check:i18n`, and Ningbo runs zh. */
                    t('batch.scopes.createCta', {
                      orders: autoBatchPreview.orders.length,
                      garments: includeRedos
                        ? autoBatchPreview.summary.totalItems
                        : autoBatchPreview.summary.totalItems - autoBatchPreview.summary.totalRedoItems,
                    })
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Animated Sticky Create Batch button — BLU-680: shared
          FloatingSelectionBar primitive (same STICKY_BUTTON enter/exit it
          used before, plus the polite live region it previously lacked).
          BLU-1330: while a run of mine is in flight the include-redos card
          becomes the live progress card (same footprint, nothing moves). With
          nothing selected (scoped create, preview, a reload mid-batch) the
          progress card floats on its own. */}
      <FloatingSelectionBar
        visible={showSelectionBar || batchCreationView !== null}
        srStatus={showSelectionBar ? t('orders.selectionCount', { count: selectedCount }) : ''}
        ariaLabel={t('orders.selectionBarAria')}
        role="region"
        pillClassName="contents"
        accessory={
          batchCreationView ? (
            <BatchCreationProgress view={batchCreationView} />
          ) : showSelectionBar ? (
            /* Opt out of pulling in redos + re-routed orders (default on = legacy).
               Manual browse mode has no preview card, so the toggle rides above the pill. */
            <div className="w-[min(22rem,calc(100vw-2rem))] rounded-2xl border bg-card px-4 py-3 shadow-lg">
              <BatchInclusionToggle
                id="batch-include-extras-orders"
                includeRedos={includeRedos}
                onIncludeRedosChange={setIncludeRedos}
                includeRequeued={includeRequeued}
                onIncludeRequeuedChange={setIncludeRequeued}
                // Real count, no longer hardcoded 0: the scope resolver's response
                // carries the location's pending-redo count, so browse mode can say
                // what the operator is actually opting out of.
                redoCount={scopeResolution?.pendingRedoCount ?? 0}
                // BLU-787: pre-cap total, so the redo row can say when
                // MAX_REDOS_PER_BATCH left some behind.
                redoTotalPending={scopeResolution?.pendingRedoTotal}
                requeuedCount={scopeResolution?.requeuedCount}
                disabled={blocksCreate}
                variant="floating"
              />
            </div>
          ) : null
        }
      >
        {showSelectionBar && (
          <Button
            onClick={() => handleCreateBatch()}
            size="lg"
            disabled={blocksCreate}
            className="shadow-lg px-6 py-4 text-base rounded-full gap-3"
          >
            {blocksCreate && (
              <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            )}
            {t('orders.createBatch', { count: selectedCount, orderWord: selectedCount === 1 ? t('picker.order').replace(':', '') : t('batches.columnOrders').toLowerCase() })}
          </Button>
        )}
      </FloatingSelectionBar>

      {/* Re-batch supersede confirmation (shared across manual + scoped flows) */}
      <SupersedeBatchConfirmDialog
        {...supersede.dialogProps}
        isPending={createBatchMutation.isPending}
      />

      {/* BLU-787 — the 200-cap trim confirm, for the direct-create path only.
          Preview does not need it: that screen already lists the trimmed set.
          Mirrors SupersedeBatchConfirmDialog's shape rather than inventing a
          second confirm language for the same button. */}
      <AlertDialog open={trimConfirmOpen} onOpenChange={setTrimConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-500">
              <AlertTriangle className="h-5 w-5" aria-hidden="true" />
              {t('batch.scopes.confirmTrimmedTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="pt-2 text-foreground">
              {t('batch.scopes.confirmTrimmedCreate', {
                taken: scopeResolution?.taken ?? 0,
                total: scopeResolution?.total ?? 0,
                remaining: (scopeResolution?.total ?? 0) - (scopeResolution?.taken ?? 0),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={createBatchMutation.isPending}>
              {t('batch.supersede.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setTrimConfirmOpen(false);
                void runScopedCreate(false);
              }}
              disabled={blocksCreate}
              className="bg-amber-600 hover:bg-amber-700 text-white focus-visible:ring-amber-600"
            >
              {t('batch.scopes.confirmTrimmedAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
